/**
 * Custom checkout — manipulacja ilością/usuwaniem produktów w order-review.
 *
 * HTML pattern (z partials/checkout/order-review.php):
 *   <span data-hn-co-qty-text>
 *     "Ilość: 2" <button data-hn-co-qty-edit>Zmień</button>
 *   </span>
 *   <span data-hn-co-qty-pill hidden>
 *     <button data-hn-co-cart-qty="dec" data-key="..."> − </button>
 *     <span class="hn-co__review-qtyval">2</span>
 *     <button data-hn-co-cart-qty="inc" data-key="..."> + </button>
 *     <button data-hn-co-cart-remove data-key="...">Usuń</button>
 *   </span>
 *
 * Flow:
 *   1. Klik "Zmień" → hide text, show pill (czysto frontend).
 *   2. Klik +/− → POST do /hn/v1/side-cart/update z nowym qty.
 *   3. Klik "Usuń" → POST z action_type='remove'.
 *   4. Po sukcesie → POST do /hn/v1/checkout/refresh-shipping żeby przeładować
 *      order-review (totale + linie z aktualnymi qty + delegowane ikony shipping).
 *   5. Delegacja na document — sekcja jest re-renderowana, fresh DOM po każdym
 *      refreshu.
 */
export function initCheckoutCartQty() {
    if (typeof window.HN_SIDECART === 'undefined' || !window.HN_SIDECART.urlSet) {
        return;
    }
    if (typeof window.HN_CHECKOUT === 'undefined' || !window.HN_CHECKOUT.restShipping) {
        return;
    }
    const sc = window.HN_SIDECART;
    const co = window.HN_CHECKOUT;

    const readAddress = () => {
        const ship = document.getElementById('ship_to_different_address');
        const prefix = (ship && ship.checked) ? 'shipping' : 'billing';
        const get = (name) => {
            const el = document.querySelector(`[name="${name}"]`);
            return el ? el.value.trim() : '';
        };
        return {
            country:  get(`${prefix}_country`)  || 'PL',
            postcode: get(`${prefix}_postcode`),
            city:     get(`${prefix}_city`),
        };
    };

    // Po każdej modyfikacji cart — pociągnij refresh order-review + shipping.
    const refreshReview = async () => {
        const addr = readAddress();
        const fd = new FormData();
        fd.append('country', addr.country);
        fd.append('postcode', addr.postcode);
        fd.append('city', addr.city);
        try {
            const res = await fetch(co.restShipping, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': co.nonce, 'Accept': 'application/json' },
                body: fd,
            });
            if (!res.ok) return;
            const data = await res.json();
            // Order-review (totale + linie items).
            const review = document.getElementById('order_review');
            if (review && data.review) {
                review.innerHTML = data.review;
            }
            // Shipping section (rates).
            const shipping = document.querySelector('[data-hn-co-section="shipping"]');
            if (shipping && data.shipping) {
                const tmp = document.createElement('div');
                tmp.innerHTML = data.shipping;
                const fresh = tmp.querySelector('[data-hn-co-section="shipping"]');
                if (fresh) {
                    shipping.replaceWith(fresh);
                    /* FIX 2026-06-11: po replaceWith fresh DOM ma `[data-hn-co-inpost-wrap]`
                     * z domyślnym style="display:none" — nawet jeśli klient ma wybranego
                     * Paczkomat (radio renderuje się jako checked z PHP session state),
                     * wrapper z buttonem "Wybierz Paczkomat" zostaje hidden.
                     *
                     * Logika identyczna z `syncInpostWrap()` w checkout-shipping-refresh.js
                     * (closure, nie exportowane — duplikat inline, simpler than refactor). */
                    const wrap = document.querySelector('[data-hn-co-inpost-wrap]');
                    if (wrap) {
                        const checked = document.querySelector('input.shipping_method:checked');
                        wrap.style.display = (checked && checked.dataset.isPaczkomat === '1') ? '' : 'none';
                    }
                }
            }
            // Sticky totals.
            if (data.fragments && data.fragments.total) {
                document.querySelectorAll('[data-hn-co-submit-total], [data-hn-co-mobile-total], [data-hn-co-grand-total]').forEach((el) => {
                    el.innerHTML = data.fragments.total;
                });
            }
        } catch (_) {
            // cicho
        }
    };

    // Cart mod: POST do side-cart/update.
    const updateCart = async (key, qty, actionType) => {
        const fd = new FormData();
        fd.append('key', key);
        if (qty !== null) fd.append('qty', String(qty));
        fd.append('action_type', actionType);
        const res = await fetch(sc.urlSet, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'X-WP-Nonce': sc.nonce, 'Accept': 'application/json' },
            body: fd,
        });
        return res.ok;
    };

    // 1. Klik "Zmień" — toggle text↔pill visibility.
    document.addEventListener('click', (e) => {
        const edit = e.target.closest && e.target.closest('[data-hn-co-qty-edit]');
        if (!edit) return;
        e.preventDefault();
        const wrap = edit.closest('[data-hn-co-qty-wrap]');
        if (!wrap) return;
        const text = wrap.querySelector('[data-hn-co-qty-text]');
        const pill = wrap.querySelector('[data-hn-co-qty-pill]');
        if (text) text.hidden = true;
        if (pill) pill.hidden = false;
    });

    // Helper: czyta aktualną qty z inputa/spana (zależnie od typu valEl).
    const readQty = (valEl) => {
        if (!valEl) return 1;
        const raw = valEl.tagName === 'INPUT' ? valEl.value : valEl.textContent;
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    };
    const writeQty = (valEl, qty) => {
        if (!valEl) return;
        if (valEl.tagName === 'INPUT') valEl.value = String(qty);
        else valEl.textContent = String(qty);
    };

    // 2. Klik + / − — zmień qty. Whitelist: tylko 'inc'/'dec'; input z
    // data-hn-co-cart-qty="set" ma osobny change handler niżej.
    document.addEventListener('click', async (e) => {
        const btn = e.target.closest && e.target.closest('[data-hn-co-cart-qty]');
        if (!btn) return;
        const action = btn.dataset.hnCoCartQty;
        if (action !== 'inc' && action !== 'dec') return; // input → no-op
        e.preventDefault();
        const key = btn.dataset.key;
        if (!key) return;
        const valEl = btn.parentElement.querySelector('.hn-co__review-qtyval');
        const current = readQty(valEl);
        // Skok o krok B2BKing (np. 6), klamrowany do min/max z inputu (stock-aware).
        const step = valEl ? Math.max(1, parseInt(valEl.step, 10) || 1) : 1;
        const min = valEl ? Math.max(1, parseInt(valEl.min, 10) || 1) : 1;
        const maxAttr = valEl ? parseInt(valEl.max, 10) : NaN;
        const max = (Number.isFinite(maxAttr) && maxAttr > 0) ? maxAttr : Infinity;
        let newQty = action === 'inc' ? current + step : current - step;
        if (newQty < min) newQty = min;
        if (newQty > max) newQty = max;
        writeQty(valEl, newQty);
        btn.disabled = true;
        const ok = await updateCart(key, newQty, 'set');
        btn.disabled = false;
        if (ok) {
            await refreshReview();
        }
    });

    // 2b. Wpisanie ręcznie w input — `change` (firefje na blur/Enter). Floor=1,
    // cap=99, snap UI gdy user wpisał śmieci. Usuwanie pozycji tylko przez „Usuń".
    document.addEventListener('change', async (e) => {
        const input = e.target.closest && e.target.closest('[data-hn-co-cart-qty="set"]');
        if (!input) return;
        const key = input.dataset.key;
        if (!key) return;
        // Dosuń do reguły B2BKing: min + wielokrotność kroku, max stock-aware.
        const step = Math.max(1, parseInt(input.step, 10) || 1);
        const min = Math.max(1, parseInt(input.min, 10) || 1);
        const maxAttr = parseInt(input.max, 10);
        const max = (Number.isFinite(maxAttr) && maxAttr > 0) ? maxAttr : Infinity;
        let qty = parseInt(input.value, 10);
        if (!Number.isFinite(qty) || qty < min) qty = min;
        else if (step > 1 && qty % step !== 0) qty = Math.ceil(qty / step) * step;
        if (qty > max) { qty = Math.floor(max / step) * step; if (qty < min) qty = min; }
        if (String(qty) !== input.value) input.value = String(qty);
        input.disabled = true;
        const ok = await updateCart(key, qty, 'set');
        input.disabled = false;
        if (ok) {
            await refreshReview();
        }
    });

    // 3. Klik "Usuń" — remove item.
    document.addEventListener('click', async (e) => {
        const btn = e.target.closest && e.target.closest('[data-hn-co-cart-remove]');
        if (!btn) return;
        e.preventDefault();
        const key = btn.dataset.key;
        if (!key) return;
        btn.disabled = true;
        const ok = await updateCart(key, null, 'remove');
        if (ok) {
            await refreshReview();
        }
    });
}
